import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { BASE_URL } from "@/config/api";
import { getAccessToken, getOrganizationId } from "@/utils/token_functions";
import { secureAxios } from "@/middleware/encryptionMiddleware";
export const createAccount = createAsyncThunk(
  "account/createAccount",
  async (accountData, { rejectWithValue }) => {
    try {
      const token = getAccessToken();

      // Transform UI data to match API structure
      const transformedData = {
        bankName: accountData.accountType,
        accountNumber: accountData.accountNumber,
        amountToDeposit: parseFloat(accountData.amount),
        spendingLimit: parseFloat(accountData.spendingAmount),
        duration: accountData.duration,
        organizationID: getOrganizationId(),
      };

      const response = await secureAxios.post(
        `${BASE_URL}/v1/account`,
        transformedData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const fetchAccounts = createAsyncThunk(
  "account/fetchAccounts",
  async ({ skip, limit }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.get(`${BASE_URL}/v1/account`, {
        params: {
          skip,
          limit,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      return {
        accounts: response.data.data,
        total: response.data.total || response.data.data.length,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const fetchAccountByAny = createAsyncThunk(
  "account/fetchAccountByAny",
  async ({ key, value }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.get(
        `${BASE_URL}/v1/account/by-any/${key}/${value}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return {
        data: response.data.data,
        total: response.data.data.length,
        isSingleAccount: key === "_id",
      };
    } catch (error) {
      if (error.response?.status === 404) {
        return { data: [], total: 0, isSingleAccount: false };
      }
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const updateAccount = createAsyncThunk(
  "account/updateAccount",
  async (accountData, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.patch(
        `${BASE_URL}/v1/account`,
        accountData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const deleteAccount = createAsyncThunk(
  "account/deleteAccount",
  async (accountId, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.delete(`${BASE_URL}/v1/account`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: [accountId],
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

const initialState = {
  accounts: [],
  fetchedAccounts: [],
  currentAccount: null,
  total: 0,
  accountDetails: {
    accountType: "",
    accountName: "",
    amount: "",
    spendingAmount: "",
    duration: "",
  },
  isLoading: false,
  error: null,
  success: false,
  isOfflineData: false,
};

const accountSlice = createSlice({
  name: "account",
  initialState,
  reducers: {
    updateAccountDetails: (state, action) => {
      state.accountDetails = {
        ...state.accountDetails,
        ...action.payload,
      };
    },
    resetAccountState: () => initialState,
    loadCachedData: (state, action) => {
      return {
        ...state,
        accounts: action.payload.accounts || state.accounts,
        currentAccount: action.payload.currentAccount || state.currentAccount,
        total: action.payload.total || state.total,
        accountDetails: action.payload.accountDetails || state.accountDetails,
        isLoading: false,
        error: null,
        isOfflineData: true,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createAccount.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createAccount.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(createAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchAccounts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAccounts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.accounts = action.payload.accounts;
        state.total = action.payload.total;
        state.error = null;
      })
      .addCase(fetchAccounts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchAccountByAny.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAccountByAny.fulfilled, (state, action) => {
        state.isLoading = false;
        state.fetchedAccounts = action.payload.data;

        if (action.payload.isSingleAccount && action.payload.data.length > 0) {
          state.currentAccount = action.payload.data[0];
        } else if (action.payload.data.length > 0) {
          state.currentAccount = action.payload.data[0];
        }

        if (!action.payload.isSingleAccount) {
          state.total = action.payload.total;
        }

        state.error = null;
      })
      .addCase(fetchAccountByAny.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateAccount.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateAccount.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(updateAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteAccount.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteAccount.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(deleteAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

// Export actions
export const { updateAccountDetails, resetAccountState, loadCachedData } =
  accountSlice.actions;

// Export selectors
export const selectAccountDetails = (state) => state.account.accountDetails;
export const selectAccountLoading = (state) => state.account.isLoading;
export const selectAccountError = (state) => state.account.error;
export const selectAccountSuccess = (state) => state.account.success;
export const selectAllAccounts = (state) => state.account.accounts;
export const selectFetchedAccounts = (state) =>
  state.account.fetchedAccounts || [];
export const selectCurrentAccount = (state) => state.account.currentAccount;
export const selectAccountTotal = (state) => state.account.total;
export const selectIsOfflineData = (state) =>
  state.account.isOfflineData || false;

export default accountSlice.reducer;
